import { Card, Eyebrow, ProvenanceTag } from "@/ui";
import { behaviorRows } from "@/mock";
import type { Row } from "@/mock";
import { useWorkspace } from "@/app/WorkspaceContext";
import { useScroll, useSummary } from "@/data/hooks";
import { PanelHeader } from "../parts";
import { AigenTag, EmptyState, SampleChip, Skeleton, panelState } from "../wiring";

/** Sample fold markers — used only on the sample fallback. */
const SAMPLE_FOLDS = [
  { label: "Hero", at: 100 },
  { label: "Features", at: 78 },
  { label: "Pricing", at: 52 },
  { label: "Testimonials", at: 34 },
  { label: "Footer CTA", at: 19 },
];

function metricValue(label: string, value: number, unit?: string): string {
  if (unit === "pct" || label.toLowerCase().includes("scroll")) return `${value}%`;
  if (unit === "sec" || label.toLowerCase().includes("session")) return `${value}s`;
  return String(value);
}

export function Behavior() {
  const { siteId, range, live } = useWorkspace();
  const scrollQ = useScroll(siteId, range);
  const summaryQ = useSummary(siteId, range);

  const state = panelState({
    siteId,
    workspaceLive: live,
    isLoading: scrollQ.isLoading || summaryQ.isLoading,
    isError: scrollQ.isError,
    hasData: !!scrollQ.data,
  });

  if (state === "loading") {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-[108px]" />
          ))}
        </div>
        <Skeleton className="h-[320px]" />
      </div>
    );
  }

  const isLive = state === "live";
  // Show the sample marker on the scroll card when the backend served seeded
  // (demo) numbers for a live-but-trafficless property.
  const seeded = !!summaryQ.data?.seeded;

  // Session-metric cards (summary-derived when live).
  const s = summaryQ.data;
  const metricRows: { label: string; value: number; unit?: string; sub?: string }[] =
    isLive && s
      ? [
          { label: "Bounce rate", value: Math.round(s.bounce_rate * 10) / 10, unit: "pct" },
          { label: "Avg. session", value: Math.round(s.avg_session_seconds), unit: "sec" },
          { label: "Pages / visit", value: Math.round(s.pages_per_visit * 10) / 10 },
          { label: "Conversion rate", value: Math.round(s.conversion_rate * 10) / 10, unit: "pct" },
        ]
      : (behaviorRows as Row[]).map((r) => ({ label: r.label, value: r.value, sub: r.sub }));

  // Scroll-depth buckets → fold rows.
  const folds: { label: string; at: number }[] =
    isLive && scrollQ.data?.buckets?.length
      ? scrollQ.data.buckets.map((b) => ({
          label: `${b.depth}% depth`,
          at: Math.round(b.reached_pct),
        }))
      : SAMPLE_FOLDS;

  const emptyScroll = isLive && (!scrollQ.data || scrollQ.data.buckets.length === 0);

  return (
    <div className="stagger space-y-6">
      <PanelHeader
        title="Behavior"
        desc="How people move through the page once they arrive."
        action={
          isLive ? <AigenTag /> : (
            <span className="flex items-center gap-2">
              <ProvenanceTag source="matomo" />
              <SampleChip />
            </span>
          )
        }
      />

      {/* session metrics */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {metricRows.map((r) => (
          <Card key={r.label} className="relative flex flex-col gap-3 p-4">
            <Eyebrow>{r.label}</Eyebrow>
            <div className="text-[28px] font-semibold leading-none tracking-tight text-ink-strong">
              {metricValue(r.label, r.value, r.unit)}
            </div>
            {r.sub && <p className="text-[12px] text-subtle">{r.sub}</p>}
            {!isLive && <SampleChip className="absolute right-3 top-3" />}
          </Card>
        ))}
      </div>

      <Card className="p-5">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-[14px] font-semibold tracking-tight text-ink-strong">
            Scroll depth
          </h3>
          {isLive ? (
            <span className="flex items-center gap-2">
              <AigenTag />
              {seeded && <SampleChip />}
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <ProvenanceTag source="matomo" />
              <SampleChip />
            </span>
          )}
        </div>
        {emptyScroll ? (
          <EmptyState
            title="No scroll data yet"
            detail="Scroll-depth buckets appear once visitors start reading your pages."
          />
        ) : (
          <div className="flex flex-col gap-3">
            {folds.map((f, i) => (
              <div key={f.label}>
                <div className="mb-1 flex items-baseline justify-between text-[13px]">
                  <span className="text-ink">{f.label}</span>
                  <span className="font-mono text-[12px] text-muted">{f.at}% reached</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-paper-2 dark:bg-white/5">
                  <div
                    className={
                      "h-full rounded-full transition-[width] duration-500 ease-out-soft " +
                      (i === folds.length - 1 ? "bg-accent" : "bg-ink-strong/70 dark:bg-white/40")
                    }
                    style={{ width: `${Math.min(100, Math.max(0, f.at))}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
